import { Stack, useRouter } from 'expo-router';
import type { ReactNode } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useClient, useMutation } from 'urql';
import { Icon as AvatarIcon } from '@/components/Custom/Avatar';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import {
  AvatarColorPalette,
  AvatarPicker,
} from '@/components/FormFields/AvatarColorPicker';
import { NameFields } from '@/components/FormFields/NameAvatarFields';
import PersonEditorScrollView from '@/components/Person/PersonEditorScrollView';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import { graphql } from '@/gql/graphql';
import useAvatarUploader from '@/hooks/useAvatarUploader';
import getOffsetColor from '@/utils/getOffsetColor';
import { GetEditablePersonProfile } from './PersonProfileQuery';

type PersonProfileValues = {
  firstName: string;
  lastName: string;
  color: string;
  avatarId: string | null;
  localAvatarUrl: string | null;
  canEdit: boolean;
};

const UpdatePersonProfile = graphql(`
  mutation UpdatePersonProfile(
    $personId: String!
    $profile: person_set_input!
  ) {
    update_person_by_pk(pk_columns: { id: $personId }, _set: $profile) {
      id
      name
      first_name
      last_name
      avatar_id
      color
    }
  }
`);

export default function PersonProfile({
  personId,
  children,
  actionContent,
}: {
  personId: string;
  children?: ReactNode;
  actionContent?: ReactNode;
}) {
  const client = useClient();
  const { back } = useRouter();
  const { uploadAvatar } = useAvatarUploader();
  const [, updateProfile] = useMutation(UpdatePersonProfile);
  const form = useForm<PersonProfileValues>({
    defaultValues: async () => {
      const { data, error } = await client.query(
        GetEditablePersonProfile,
        { personId },
        { requestPolicy: 'network-only' },
      );
      const person = data?.person[0];
      return {
        firstName: person?.first_name ?? '',
        lastName: person?.last_name ?? '',
        color: person?.color ?? '',
        avatarId: person?.avatar_id ?? null,
        localAvatarUrl: null,
        canEdit: Boolean(person && !error),
      };
    },
  });
  const { control, setValue, handleSubmit, setError, clearErrors, formState } =
    form;
  const values = useWatch({ control });
  const { isLoading, isDirty, isSubmitting, errors, defaultValues } = formState;
  const reportError = () =>
    setError('root.save', {
      message: 'Could not save this profile. Please try again.',
    });
  const save = async (profile: PersonProfileValues) => {
    clearErrors('root.save');
    if (!defaultValues?.canEdit || !profile.firstName.trim()) return;
    let avatarId = profile.avatarId;
    if (profile.localAvatarUrl) {
      const upload = await uploadAvatar({
        localUrl: profile.localAvatarUrl,
        path: `user/${personId}/profile`,
      });
      if (!upload.ok) return reportError();
      avatarId = upload.avatarId;
    }
    const { data, error } = await updateProfile({
      personId,
      profile: {
        first_name: profile.firstName.trim(),
        last_name: profile.lastName.trim() || null,
        avatar_id: avatarId,
        color: profile.color,
      },
    });
    if (error || data?.update_person_by_pk?.id !== personId)
      return reportError();
    back();
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{ title: '', headerBackButtonDisplayMode: 'minimal' }}
      />
      <FormContentBoundary loading={isLoading}>
        {defaultValues?.canEdit ? (
          <>
            <PersonEditorScrollView
              actionContent={actionContent}
              name={[values.firstName, values.lastName]
                .filter(Boolean)
                .join(' ')}
              avatarId={values.avatarId}
              color={values.color}
              masthead={
                <AvatarPicker
                  avatarId={values.avatarId}
                  avatarSize="3xl"
                  nameSize="4xl"
                  nameNumberOfLines={0}
                  name={[values.firstName, values.lastName]
                    .filter(Boolean)
                    .join(' ')}
                  color={values.color}
                  localAvatarUrl={values.localAvatarUrl}
                  onChangeLocalAvatarUrl={(localAvatarUrl) =>
                    setValue('localAvatarUrl', localAvatarUrl, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                >
                  <AvatarIcon
                    name="camera-outline"
                    size="5xl"
                    color={getOffsetColor(values.color || '', false)}
                  />
                </AvatarPicker>
              }
            >
              <Box p="lg" space="md" testID="PersonProfileEditor">
                <Box align="center" py="lg">
                  <AvatarColorPalette
                    color={values.color}
                    onChangeColor={(color) =>
                      setValue('color', color, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                  />
                </Box>
                <NameFields
                  firstName={values.firstName ?? ''}
                  lastName={values.lastName ?? ''}
                  onChangeFirstName={(name) =>
                    setValue('firstName', name, { shouldDirty: true })
                  }
                  onChangeLastName={(name) =>
                    setValue('lastName', name, { shouldDirty: true })
                  }
                />

                {errors.root?.save?.message && (
                  <Text role="alert" variant="error">
                    {errors.root.save.message}
                  </Text>
                )}
              </Box>
              {children}
            </PersonEditorScrollView>
            <ModalKeyboardStickyView>
              <FormSaveButton
                info={{ text: 'Save' }}
                isDisabled={
                  !isDirty || isSubmitting || !values.firstName?.trim()
                }
                onPress={handleSubmit((profile) =>
                  save(profile).catch(reportError),
                )}
              />
            </ModalKeyboardStickyView>
          </>
        ) : (
          <Box p="lg">
            <Text>This profile is unavailable or you cannot edit it.</Text>
          </Box>
        )}
      </FormContentBoundary>
    </FormProvider>
  );
}
